/**
 * Approximate geographic centre of each US state, for opening a map on a property
 * we have not been able to locate precisely.
 *
 * WHY THIS EXISTS: a property whose address will not geocode used to render a
 * placeholder panel — which is "defaulting to nothing", the exact thing the map
 * work set out to remove. Showing the right STATE at low zoom is honest and
 * orienting; showing a dashed box is neither. The UI always labels this as
 * approximate and never drops a pin on it, because there is no point to pin.
 *
 * These are display centroids, deliberately not precise: they exist to choose a
 * sensible initial viewport, never to represent where anything is. Nothing is ever
 * persisted from this table — `properties.latitude/longitude` is only ever written
 * from a real geocode.
 */

export const STATE_CENTROIDS: Record<string, { lat: number; lon: number }> = {
  AL: { lat: 32.81, lon: -86.79 }, AK: { lat: 61.37, lon: -152.40 },
  AZ: { lat: 33.73, lon: -111.43 }, AR: { lat: 34.97, lon: -92.37 },
  CA: { lat: 36.12, lon: -119.68 }, CO: { lat: 39.06, lon: -105.31 },
  CT: { lat: 41.60, lon: -72.76 }, DE: { lat: 39.32, lon: -75.51 },
  DC: { lat: 38.90, lon: -77.03 }, FL: { lat: 27.77, lon: -81.69 },
  GA: { lat: 33.04, lon: -83.64 }, HI: { lat: 21.09, lon: -157.50 },
  ID: { lat: 44.24, lon: -114.48 }, IL: { lat: 40.35, lon: -88.99 },
  IN: { lat: 39.85, lon: -86.26 }, IA: { lat: 42.01, lon: -93.21 },
  KS: { lat: 38.53, lon: -96.73 }, KY: { lat: 37.67, lon: -84.67 },
  LA: { lat: 31.17, lon: -91.87 }, ME: { lat: 44.69, lon: -69.38 },
  MD: { lat: 39.06, lon: -76.80 }, MA: { lat: 42.23, lon: -71.53 },
  MI: { lat: 43.33, lon: -84.54 }, MN: { lat: 45.69, lon: -93.90 },
  MS: { lat: 32.74, lon: -89.68 }, MO: { lat: 38.46, lon: -92.29 },
  MT: { lat: 46.92, lon: -110.45 }, NE: { lat: 41.13, lon: -98.27 },
  NV: { lat: 38.31, lon: -117.06 }, NH: { lat: 43.45, lon: -71.56 },
  NJ: { lat: 40.30, lon: -74.52 }, NM: { lat: 34.84, lon: -106.25 },
  NY: { lat: 42.17, lon: -74.95 }, NC: { lat: 35.63, lon: -79.81 },
  ND: { lat: 47.53, lon: -99.78 }, OH: { lat: 40.39, lon: -82.76 },
  OK: { lat: 35.57, lon: -96.93 }, OR: { lat: 44.57, lon: -122.07 },
  PA: { lat: 40.59, lon: -77.21 }, RI: { lat: 41.68, lon: -71.51 },
  SC: { lat: 33.86, lon: -80.95 }, SD: { lat: 44.30, lon: -99.44 },
  TN: { lat: 35.75, lon: -86.69 }, TX: { lat: 31.05, lon: -97.56 },
  UT: { lat: 40.15, lon: -111.86 }, VT: { lat: 44.05, lon: -72.71 },
  VA: { lat: 37.77, lon: -78.17 }, WA: { lat: 47.40, lon: -121.49 },
  WV: { lat: 38.49, lon: -80.95 }, WI: { lat: 44.27, lon: -89.62 },
  WY: { lat: 42.76, lon: -107.30 },
};

/** Zoom that frames a whole state. Deliberately coarse — it is not a location. */
export const STATE_ZOOM = 5.5;

/**
 * A viewport to open on when a property has no coordinates, or null when even the
 * state is unknown (in which case there is genuinely nothing to show).
 */
export function fallbackViewFor(state: string | null | undefined) {
  if (!state) return null;
  return STATE_CENTROIDS[String(state).trim().toUpperCase()] ?? null;
}
